import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import Seo from "../seo";
import { api, CATEGORY_MEDIA } from "../api";
import { Reveal, Chapter } from "../components/motion";

export default function Conditions() {
    const [conditions, setConditions] = useState([]);

    useEffect(() => {
        api.get("/content/conditions").then((r) => setConditions(r.data.conditions)).catch(() => {});
    }, []);

    const groups = useMemo(() => {
        const map = new Map();
        conditions.forEach((c) => {
            const cats = c.category.split(",").map((s) => s.trim());
            cats.forEach((cat) => {
                if (!map.has(cat)) map.set(cat, []);
                if (!map.get(cat).some((x) => x.slug === c.slug)) map.get(cat).push(c);
            });
        });
        return [...map.entries()];
    }, [conditions]);

    return (
        <div className="mx-auto max-w-7xl px-5 py-14 lg:px-10 lg:py-20">
            <Seo title="Urology Conditions | Dr. Krishnakanth AVB, Madurai"
                description="Patient-friendly explanations of common urological conditions — kidney stones, prostate conditions, urethral stricture, urological cancers and pediatric urology." />
            <Chapter index={1} label="Urology Conditions" />
            <Reveal>
                <h1 className="mt-8 max-w-2xl font-display text-4xl font-medium leading-tight text-navy sm:text-5xl">
                    Conditions we evaluate and treat
                </h1>
                <p className="mt-6 max-w-2xl text-base leading-relaxed text-steel md:text-lg">
                    Clear, plain-language explanations of common urological conditions. This information is general
                    education — individual advice requires a consultation.
                </p>
            </Reveal>

            <div className="mt-16 space-y-20">
                {groups.map(([category, items], gi) => {
                    const media = CATEGORY_MEDIA[category] || CATEGORY_MEDIA["Kidney & Urinary Tract"];
                    return (
                    <section key={category} data-testid={`condition-category-${gi}`}>
                        <Reveal>
                            <div className="relative overflow-hidden rounded-2xl">
                                <img src={media.img} alt={media.alt} loading="lazy" className="h-36 w-full object-cover sm:h-44" />
                                <div className="absolute inset-0 bg-gradient-to-r from-navy/85 via-navy/60 to-navy/20" aria-hidden="true" />
                                <h2 className="absolute inset-0 flex items-center gap-4 px-7 font-display text-2xl font-medium text-white sm:text-3xl">
                                    <span className="font-mono-label text-white/70 text-sm">{String(gi + 1).padStart(2, "0")}</span>
                                    {category}
                                </h2>
                                <div className={`absolute inset-x-0 bottom-0 h-1.5 ${media.bar}`} aria-hidden="true" />
                            </div>
                        </Reveal>
                        <div className="mt-8 grid gap-px overflow-hidden rounded-2xl border border-border bg-border sm:grid-cols-2 lg:grid-cols-3">
                            {items.map((c, i) => (
                                <Reveal key={c.slug} delay={Math.min(i * 0.04, 0.3)} y={16}>
                                    <div className="flex h-full flex-col bg-white p-7 transition-colors duration-300 hover:bg-mist">
                                        <h3 className="font-display text-lg font-medium leading-snug text-navy">{c.name}</h3>
                                        <p className="mt-3 flex-1 text-sm leading-relaxed text-steel">{c.summary}</p>
                                        <Link to={`/conditions/${c.slug}`} data-testid={`condition-${c.slug}-link`}
                                            className="group mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-accent">
                                            Learn More
                                            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                                        </Link>
                                    </div>
                                </Reveal>
                            ))}
                        </div>
                    </section>
                    );
                })}
            </div>
        </div>
    );
}
